import { memo, useCallback, useMemo } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import styles from "./FileUploadPB.module.scss";
import { Task, TaskStatus, TaskType } from "../../../../../contexts/TasksContext";
import { useTasksContext } from "../../../../../contexts/TasksContext";
import { useChatContext } from "../../../../../contexts/ChatContext";
import type { Thread } from "../../../../../types/types";
import { useLocation, useNavigate } from "react-router-dom";
import useFetchWithMsal from "../../../../../hooks/useFetchWithMsal";
import { cancelExhaustiveSearchJob } from "../../../../../utils/legalSearchExhaustiveCancel";

const FORCE_CLOSE_PANEL_THREAD_KEY = "infravia-force-close-panel-thread-id";

type UiPhase = "completed" | "failed" | "running" | "pending" | "cancelled";

const badgeClass: Record<UiPhase, string> = {
  completed: styles.taskBadgeCompleted,
  failed: styles.taskBadgeFailed,
  running: styles.taskBadgeRunning,
  pending: styles.taskBadgePending,
  cancelled: styles.taskBadgeFailed,
};

const fillClass: Record<UiPhase, string> = {
  completed: styles.taskProgressCompleted,
  failed: styles.taskProgressFailed,
  running: styles.taskProgressRunning,
  pending: styles.taskProgressPending,
  cancelled: styles.taskProgressFailed,
};

const phaseLabelEn: Record<UiPhase, string> = {
  completed: "Completed",
  failed: "Failed",
  running: "In progress",
  pending: "Preparing",
  cancelled: "Cancelled",
};

function cardTitle(task: Task): string {
  switch (task.type) {
    case TaskType.LEGAL_SEARCH:
      return `${resolveModeLabel(task.page ?? task.mode)} search`;
    case TaskType.UPLOAD:
      return "Upload & summary";
    case TaskType.SUMMARY:
      return "Summary";
    default:
      return "Task";
  }
}

function resolveModeLabel(raw?: string): string {
  const p = String(raw || "")
    .trim()
    .toLowerCase();
  if (p === "mydeal") return "Deal";
  if (p === "market") return "Market";
  if (p === "legal") return "Legal";
  if (p === "oreima") return "Oreima";
  return "—";
}

function resolvePhase(task: Task): UiPhase {
  if (task.status === TaskStatus.ERROR) return "failed";
  if (task.status === TaskStatus.CANCELLED) return "cancelled";
  if (task.status === TaskStatus.COMPLETED) return "completed";
  if (
    task.status === TaskStatus.PROCESSING &&
    task.file.nbProcessed === 0
  ) {
    return "pending";
  }
  return "running";
}

const LEGAL_SEARCH_STEPS = ["Preparation", "Documents", "Synthesis", "Done"] as const;

/** Current step index for legal/exhaustive search UI; -2 = all steps completed; -3 = cancelled/error (hide stepper). */
function legalSearchActiveStepIndex(task: Task): number {
  if (task.status === TaskStatus.COMPLETED) return -2;
  if (
    task.status === TaskStatus.CANCELLED ||
    task.status === TaskStatus.ERROR
  ) {
    return -3;
  }
  if (task.status === TaskStatus.SUMMARIZING) return 2;
  const phase = String(task.legalWipPhase || "").toLowerCase();
  const detail = String(task.legalProgressDetail || "").toLowerCase();
  if (
    phase === "subquestions_done" ||
    detail === "preparation done: sub-questions generated."
  ) {
    return 1;
  }
  if (phase === "synthesis" || phase === "summarizing") return 2;
  if (phase === "documents" || phase === "rag_panels") return 1;
  if (phase === "preparation") return 0;
  if (task.status === TaskStatus.PROCESSING && task.file.nbProcessed === 0)
    return 0;
  if (task.status === TaskStatus.PROCESSING) return 1;
  return 0;
}

function LegalSearchStepper({ task }: { task: Task }) {
  const idx = legalSearchActiveStepIndex(task);
  if (idx === -3) return null;
  return (
    <div className={styles.taskStepper} aria-label="Task steps">
      {LEGAL_SEARCH_STEPS.map((label, i) => {
        const allDone = idx === -2;
        const done = allDone || (idx >= 0 && i < idx);
        const active = !allDone && idx === i;
        return (
          <span
            key={label}
            className={`${styles.taskStepperStep} ${
              done ? styles.taskStepperStepDone : ""
            } ${active ? styles.taskStepperStepActive : ""}`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

const TaskProgressBar = memo(
  ({
    task,
    setSelectedMenu,
    openSummary,
    setSelectedMode,
    showLastActivity = false,
  }: {
    task: Task;
    setSelectedMenu: (selectedMenu: number) => void;
    openSummary: () => void;
    setSelectedMode: (selectedMode: string) => void;
    /** Affiche « Last activity » (temps depuis la dernière MAJ) — réservé au DevMode chat. */
    showLastActivity?: boolean;
  }) => {
    const { removeTask, updateTask, setTasks } = useTasksContext();
    const { changeThread, threads } = useChatContext();
    const { execute } = useFetchWithMsal();
    const navigate = useNavigate();
    const location = useLocation();

    const cancelLegalSearch = useCallback(async () => {
      if (task.type !== TaskType.LEGAL_SEARCH || !task.threadId) return;
      await cancelExhaustiveSearchJob(execute, {
        threadId: task.threadId,
        taskId: task.id,
        setTasks,
      });
    }, [task, execute, setTasks]);

    const openLegalConversation = useCallback(() => {
      const tid = task.threadId;
      if (!tid) return;
      try {
        sessionStorage.setItem(FORCE_CLOSE_PANEL_THREAD_KEY, tid);
      } catch {
        // ignore storage errors (private mode/quota)
      }

      // Les tâches serveur portent `page`/`mode` (legal/market/mydeal/oreima).
      const rawTarget = String(task.page ?? task.mode ?? "")
        .trim()
        .toLowerCase();
      const targetPage = rawTarget === "deal" ? "mydeal" : rawTarget;
      const allowed = new Set(["market", "mydeal", "legal", "oreima"]);

      const currentPage = location.pathname.replace(/^\//, "").trim();
      const shouldNavigate = allowed.has(targetPage) && currentPage !== targetPage;

      if (shouldNavigate) {
        setSelectedMenu(0);
        navigate(`/${targetPage}?threadId=${encodeURIComponent(tid)}`);
        return;
      }

      // Même page : ouvrir directement (évite un double passage par l’URL).
      const existing = threads.find((t) => t.id === tid);
      const thread: Thread =
        existing ??
        ({
          id: tid,
          title: task.file.filename,
          updatedAt: new Date(task.updatedAt),
        } as Thread);
      changeThread(thread);
      setSelectedMenu(0);
    }, [
      task.threadId,
      task.page,
      task.mode,
      task.file.filename,
      task.updatedAt,
      threads,
      changeThread,
      setSelectedMenu,
      navigate,
      location.pathname,
    ]);

    const file = task.file;

    const phase = resolvePhase(task);
    const showStatusSpinner = phase === "running" || phase === "pending";
    const badgeLabel = useMemo(() => {
      if (
        task.type === TaskType.LEGAL_SEARCH &&
        task.status === TaskStatus.COMPLETED &&
        file.nbProcessed < file.nbPages
      ) {
        return "Partial";
      }
      return phaseLabelEn[phase];
    }, [task.type, task.status, file.nbProcessed, file.nbPages, phase]);

    const statusDetail = useMemo(() => {
      if (task.type === TaskType.LEGAL_SEARCH) {
        if (task.status === TaskStatus.CANCELLED) {
          return "This task was cancelled.";
        }
        if (task.status === TaskStatus.ERROR) {
          return "Something went wrong. Please try again.";
        }
        if (task.status === TaskStatus.COMPLETED) {
          if (file.nbProcessed < file.nbPages) {
            return "Search stopped early (e.g. refresh or another tab) — partial result.";
          }
          return null;
        }
        if (task.status === TaskStatus.SUMMARIZING) {
          return "Building comparison table…";
        }
        return null;
      }
      if (task.type === TaskType.SUMMARY) {
        if (task.status === TaskStatus.ERROR) {
          return "Something went wrong. Please try again.";
        }
        return task.status === TaskStatus.SUMMARIZING
          ? "Summarizing…"
          : task.status === TaskStatus.COMPLETED
            ? null
            : "Processing…";
      }
      if (task.status === TaskStatus.CANCELLED) {
        return "Task cancelled.";
      }
      if (task.status === TaskStatus.ERROR) {
        return "Something went wrong. Please try again.";
      }
      return task.status === TaskStatus.PROCESSING
        ? "Uploading document…"
        : task.status === TaskStatus.SUMMARIZING
          ? "Document ready — summarizing…"
          : task.status === TaskStatus.COMPLETED
            ? null
            : "Processing…";
    }, [task.type, task.status, file.nbProcessed, file.nbPages]);

    const progressPercent = useMemo(() => {
      if (task.type === TaskType.LEGAL_SEARCH) {
        return file.nbPages > 0
          ? Math.min(100, Math.round((file.nbProcessed / file.nbPages) * 100))
          : 0;
      }
      if (file.nbPages <= 0) return 0;
      const denom = (task.type === TaskType.SUMMARY ? 1 : 2) * file.nbPages;
      return Math.min(
        100,
        Math.round(((file.nbProcessed + file.nbProcessedSummary) / denom) * 100)
      );
    }, [
      task.type,
      file.nbPages,
      file.nbProcessed,
      file.nbProcessedSummary,
    ]);

    const docsLabel = useMemo(() => {
      if (task.type === TaskType.LEGAL_SEARCH) {
        return `${file.nbProcessed}/${file.nbPages}`;
      }
      if (file.status === "processing") {
        return `${file.nbProcessed}/${file.nbPages}`;
      }
      return `${file.nbProcessedSummary}/${file.nbPages}`;
    }, [task.type, file]);

    const datesDifferenceInMilliseconds = (date1: Date, date2: Date) =>
      Math.abs(date1.getTime() - date2.getTime());

    const formatTime = (ms: number) => {
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((ms % (1000 * 60)) / 1000);
      const result: string[] = [];
      if (hours > 0) result.push(`${hours}h`);
      if (minutes > 0) result.push(`${minutes}m`);
      if (seconds > 0 || result.length === 0) result.push(`${seconds}s`);
      return result.join(" ");
    };

    const resetTask = () => {
      if (task.type === TaskType.LEGAL_SEARCH) return;
      task.status = TaskStatus.PROCESSING;
      task.createdAt = Date.now();
      task.updatedAt = Date.now();
      task.file.nbProcessedSummary = 0;
      updateTask(task);
    };

    const nbMinutesBeforeProposeRetry = 5;
    const proposeRetry = () => {
      if (task.type === TaskType.LEGAL_SEARCH) return false;
      if (task.status === TaskStatus.COMPLETED || task.type === TaskType.UPLOAD)
        return false;
      return (
        datesDifferenceInMilliseconds(
          new Date(),
          new Date(task.updatedAt)
        ) >
        nbMinutesBeforeProposeRetry * 60 * 1000
      );
    };

    const primaryAction = useMemo(() => {
      if (task.type === TaskType.LEGAL_SEARCH && task.threadId) {
        if (task.status !== TaskStatus.COMPLETED) return null;
        return {
          label: "Open answer",
          onClick: openLegalConversation,
        };
      }
      if (
        task.type === TaskType.UPLOAD &&
        (task.file.status === "summarizing" || task.file.status === "summary_done")
      ) {
        return {
          label: "View document",
          onClick: () => {
            setSelectedMenu(1);
            setSelectedMode("MYGEORGES");
          },
        };
      }
      if (task.status === TaskStatus.COMPLETED && task.type !== TaskType.LEGAL_SEARCH) {
        return { label: "View summary", onClick: openSummary };
      }
      return null;
    }, [
      task,
      openLegalConversation,
      openSummary,
      setSelectedMenu,
      setSelectedMode,
    ]);

    const createdStr = new Date(task.createdAt).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const queryOrFileLabel =
      task.type === TaskType.LEGAL_SEARCH ? "Query:" : "File:";

    return (
      <div className={styles.taskCard}>
        <div className={styles.taskCardInner}>
          <div className={styles.taskCardMain}>
            <div className={styles.taskCardTitleRow}>
              <h2 className={styles.taskCardTitle}>{cardTitle(task)}</h2>
              <span
                className={`${styles.taskBadge} ${badgeClass[phase]} ${
                  showStatusSpinner ? styles.taskBadgeWithSpinner : ""
                }`}
              >
                {badgeLabel}
                {showStatusSpinner ? (
                  <span className={styles.taskStatusSpinner} aria-hidden="true" />
                ) : null}
              </span>
            </div>

            <p className={styles.taskQuery}>
              <span className={styles.taskQueryLabel}>{queryOrFileLabel}</span>{" "}
              {file.filename}
            </p>

            <div className={styles.taskMeta}>
              <div>
                <span className={styles.taskMetaLabel}>Created:</span>{" "}
                {createdStr}
              </div>
              <div className={styles.taskMetaRowWithAction}>
                <span>
                  <span className={styles.taskMetaLabel}>Task ID:</span>{" "}
                  <code>{task.id}</code>
                </span>
              </div>
              <div>
                <span className={styles.taskMetaLabel}>
                  {task.type === TaskType.LEGAL_SEARCH
                    ? "Documents:"
                    : "Pages:"}
                </span>{" "}
                {docsLabel}
              </div>
              {task.type === TaskType.LEGAL_SEARCH &&
              task.legalWipJobFormula ? (
                <div>
                  <span className={styles.taskMetaLabel}>RAG job:</span>{" "}
                  <code>{task.legalWipJobFormula}</code>
                  <span className={styles.taskMetaHint}>
                    {" "}
                    (docs×cols÷batch)
                  </span>
                </div>
              ) : null}
            </div>

            {statusDetail ? (
              <div className={styles.taskDetailLine}>{statusDetail}</div>
            ) : null}

            {task.type === TaskType.LEGAL_SEARCH ? (
              <LegalSearchStepper task={task} />
            ) : null}

            {task.status !== TaskStatus.COMPLETED &&
            task.status !== TaskStatus.CANCELLED ? (
              <>
                {showLastActivity ? (
                  <div className={styles.taskDetailLine}>
                    <span className={styles.taskMetaLabel}>Last activity:</span>{" "}
                    {formatTime(
                      datesDifferenceInMilliseconds(
                        new Date(),
                        new Date(task.updatedAt)
                      )
                    )}
                    {proposeRetry() ? (
                      <>
                        {" "}
                        <button
                          type="button"
                          className={styles.taskSecondaryButton}
                          style={{
                            marginLeft: "0.5rem",
                            padding: "0.15rem 0.45rem",
                          }}
                          onClick={() => resetTask()}
                        >
                          Retry
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : proposeRetry() ? (
                  <div className={styles.taskDetailLine}>
                    <button
                      type="button"
                      className={styles.taskSecondaryButton}
                      style={{ padding: "0.15rem 0.45rem" }}
                      onClick={() => resetTask()}
                    >
                      Retry
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            <div className={styles.taskProgressTrack}>
              <div
                className={`${styles.taskProgressFill} ${fillClass[phase]}`}
                style={{
                  width: `${
                    task.status === TaskStatus.ERROR ||
                    task.status === TaskStatus.CANCELLED
                      ? 100
                      : progressPercent
                  }%`,
                }}
              />
            </div>
          </div>

          <div className={styles.taskCardActions}>
            {task.type === TaskType.LEGAL_SEARCH &&
            (task.status === TaskStatus.PROCESSING ||
              task.status === TaskStatus.SUMMARIZING) ? (
              <button
                type="button"
                className={styles.taskSecondaryButton}
                onClick={() => void cancelLegalSearch()}
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              className={styles.taskSecondaryButton}
              onClick={() => removeTask(task)}
            >
              Dismiss
            </button>
            {primaryAction ? (
              <button
                type="button"
                className={styles.taskPrimaryButton}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default TaskProgressBar;
